// Generated from doskai-contracts/contracts/policies/marketplace-reporting-policy.ts. Do not edit; run marketplace-reporting-policy:sync.
// Read-only reporting for marketplace summaries; never changes payment or fulfillment records.
export const key = (value: unknown) =>
  String(value || 'unknown')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_');
export const number = (value: unknown) => (Number.isFinite(Number(value)) ? Number(value) : 0);
export const timestamp = (value: any) => {
  if (value == null || value === '') return 0;
  return Number.isFinite(Number(value)) ? Number(value) : Date.parse(value) || 0;
};
export function reportingRange(data: any = {}, now = Date.now()) {
  const ranges: Record<string, number> = {
    day: 1,
    today: 1,
    week: 7,
    month: 30,
    quarter: 90,
    year: 365,
  };
  const days = ranges[data.dateRange];
  const start = data.startDate ? timestamp(data.startDate) : days ? now - days * 86400000 : 0;
  const end = data.endDate ? timestamp(data.endDate) + (/^\d{4}-\d{2}-\d{2}$/.test(data.endDate) ? 86399999 : 0) : now;
  if (start > end) throw new Error('Invalid reporting date range');
  return { start, end };
}
export function currencyOf(record: any) {
  const value = String(
    record.chargeCurrencyCode ||
      record.payingPartyCurrencyCode ||
      record.bookerCurrencyCode ||
      record.buyerCurrencyCode ||
      record.currencyCode ||
      record.currency ||
      ''
  )
    .trim()
    .toUpperCase();
  return /^[A-Z]{3}$/.test(value) ? value : null;
}
export function majorAmount(minor: number, currency: string | null) {
  const digits =
    new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency || 'USD',
    }).resolvedOptions().maximumFractionDigits ?? 2;
  return minor / 10 ** digits;
}
export function chargedMoney(record: any) {
  const raw = record.amountTotalMinor ?? record.totalAmount ?? record.total;
  const minor = Number(raw);
  const currency = currencyOf(record);
  return {
    amount: Number.isSafeInteger(minor) && minor >= 0 ? majorAmount(minor, currency) : 0,
    currency,
    amountKnown: raw != null && Number.isSafeInteger(minor) && minor >= 0,
    known: raw != null && Number.isSafeInteger(minor) && minor >= 0 && !!currency,
  };
}
export function moneySummary(entries: any[]) {
  const totals: Record<string, number> = {},
    counts: Record<string, number> = {};
  let missing = 0;
  for (const money of entries) {
    if (!money.known) {
      missing++;
      continue;
    }
    totals[money.currency] = (totals[money.currency] || 0) + money.amount;
    counts[money.currency] = (counts[money.currency] || 0) + 1;
  }
  const rounded = Object.fromEntries(
    Object.entries(totals).map(([currency, amount]) => [currency, Math.round(amount * 1000) / 1000])
  );
  const averages = Object.fromEntries(
    Object.entries(rounded).map(([currency, amount]) => [currency, amount / counts[currency]])
  );
  const currencies = Object.keys(rounded);
  return {
    value: currencies.length === 1 ? rounded[currencies[0]] : 0,
    average: currencies.length === 1 ? averages[currencies[0]] : 0,
    currency: currencies.length === 1 ? currencies[0] : null,
    totalsByCurrency: rounded,
    averagesByCurrency: averages,
    missing,
  };
}
export const GROUPS: Record<string, Record<string, string[]>> = {
  collabs: {
    active: ['active', 'booked', 'accepted', 'confirmed', 'progress', 'in_progress'],
    pending: ['pending', 'requested', 'open'],
    completed: ['complete', 'completed', 'finished', 'succeeded', 'paid', 'booker_reviewed', 'receiver_reviewed'],
    cancelled: ['cancelled', 'canceled', 'rejected'],
    issue: [
      'refund_requested',
      'refund_processing',
      'refund_failed',
      'refund_declined',
      'refund_canceled',
      'refund_cancelled',
      'refunded',
      'expired',
      'failed',
      'disputed',
    ],
  },
  ads: {
    active: ['active', 'running', 'live', 'booked', 'progress', 'in_progress'],
    pending: ['pending', 'review', 'in_review', 'draft', 'requested'],
    completed: ['complete', 'completed', 'finished', 'succeeded', 'expired'],
    paused: ['pause', 'paused'],
    failed: ['failed', 'rejected', 'declined', 'cancelled', 'canceled'],
  },
  orders: {
    pending: ['pending', 'booked', 'requested'],
    processing: ['processing', 'confirmed', 'accepted', 'ready'],
    shipped: ['shipped', 'in_transit'],
    delivered: [
      'delivered',
      'picked',
      'picked_up',
      'pickedup',
      'pickup',
      'fulfilled',
      'complete',
      'completed',
      'buyer_reviewed',
      'seller_reviewed',
    ],
    cancelled: ['cancelled', 'canceled', 'rejected'],
    refunded: ['refunded'],
  },
  rentals: {
    pending: ['pending', 'booked', 'requested', 'open'],
    confirmed: ['confirmed', 'approved', 'accepted', 'ready'],
    active: ['active', 'in_progress', 'ongoing', 'progress', 'rented_in', 'picked'],
    completed: ['complete', 'completed', 'delivered', 'fulfilled', 'finished', 'returned', 'rented_out'],
    cancelled: ['cancelled', 'canceled', 'rejected'],
    refunded: ['refunded', 'refund_requested'],
  },
};
export function summaryGroup(type: string, record: any) {
  const status = key(record.status);
  if (type === 'rentals' && ['buyer_reviewed', 'seller_reviewed'].includes(status)) {
    return record.returnedDate || record.rentedOutDate
      ? 'completed'
      : record.pickedDate || record.rentedInDate
        ? 'active'
        : 'other';
  }
  return Object.entries(GROUPS[type]).find(([, statuses]) => statuses.includes(status))?.[0] || 'other';
}
export function rentalDays(record: any) {
  const from = timestamp(record.startDate),
    to = timestamp(record.endDate);
  if (from && to >= from) return Math.max(1, Math.ceil((to - from) / 86400000));
  const days = Number(record.rentalDays);
  return Number.isFinite(days) && days > 0 ? days : 0;
}

// Gross charge reporting is distinct from platform income and fulfillment status.
export function paymentGroup(record: any, type: string) {
  const status = key(record.paymentStatus);
  if (['succeeded', 'paid', 'complete', 'completed'].includes(status)) return 'captured';
  if (['failed', 'canceled', 'cancelled'].includes(status)) return 'failed';
  if (['processing', 'pending', 'requires_action', 'requires_payment_method'].includes(status)) return 'processing';
  if (status === 'refunded') return 'refunded';
  // Native ad purchases persist campaign Active after verified payment.
  if (type === 'ads' && key(record.status) === 'active') return 'captured';
  return 'unknown';
}
export function completedCharge(type: string, record: any) {
  // Orders retains the current lifecycle status; refund cancellation is not fulfillment.
  if (type === 'orders') {
    const status = key(record.status);
    const rental = key(record.subType) === 'rental';
    if (rental && ['returned', 'rented_out', 'buyer_reviewed', 'seller_reviewed'].includes(status))
      return timestamp(record.returnedDate || record.rentedOutDate) > 0;
    if (['refund_canceled', 'refund_cancelled'].includes(status))
      return rental
        ? timestamp(record.returnedDate || record.rentedOutDate) > 0
        : timestamp(record.deliveredDate || record.pickedDate) > 0;
  }
  return type === 'ads'
    ? ['active', 'completed', 'paused'].includes(summaryGroup(type, record))
    : summaryGroup(type, record) === (type === 'orders' ? 'delivered' : 'completed');
}

/** Shared card membership for canonical metrics and transaction search-index facets. */
export function transactionRevenueEligible(type: string, record: any): boolean {
  return type === 'orders'
    ? completedCharge('orders', record) && paymentGroup(record, 'orders') === 'captured'
    : type === 'rentals' && summaryGroup('rentals', record) === 'completed';
}

/** Search-only membership; it never asserts provider verification or stores monetary facts. */
export function transactionMetricTypes(type: string, record: any): string[] {
  if (!Object.hasOwn(GROUPS, type) || !record) return [];
  return [
    ...new Set([
      'all',
      summaryGroup(type, record),
      ...(transactionRevenueEligible(type, record) ? ['totalRevenue'] : []),
    ]),
  ];
}

/** Canonical marketplace list facts. Provider verification belongs to exact server detail reads. */
export function marketplaceTransactionRow(
  domain: string,
  id: string,
  record: any,
  buyer: any = {},
  seller: any = {}
): any {
  if (!record || !['ads', 'collabs', 'orders', 'rentals'].includes(domain)) return null;
  const marked = record.qaRunner === true || record.isTestData === true || record.testRecord === true;
  const references = [
    'providerPaymentId',
    'stripePaymentIntentId',
    'providerChargeId',
    'stripeChargeId',
    'stripeInvoiceId',
    'providerInvoiceId',
    'paymentIntentId',
  ];
  if (
    /^(qa_|demo_)/.test(id) &&
    marked &&
    ![record, record.details || {}].some((value) => references.some((field) => !!value[field]))
  )
    return null;
  if (domain === 'rentals' && key(record.itemType || record.feedType) !== 'location') return null;
  const ads = domain === 'ads',
    collabs = domain === 'collabs';
  const items: any[] = Object.values(record.items || {}).filter((item: any) => item && typeof item === 'object');
  const first = items[0];
  const title = ads
    ? record.adTitle || record.title || 'Untitled'
    : collabs
      ? record.requestTitle || record.postTitle || record.collabTitle || 'Untitled collaboration'
      : `${first?.postTitle || first?.itemTitle || first?.title || record.itemTitle || record.postTitle || record.locationTitle || 'Item'}${items.length > 1 ? ` + ${items.length - 1} more` : ''}`;
  const amount = [record.amountTotalMinor, record.total].find(
    (value) => value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))
  );
  const status = String(record.status || 'unknown')
    .trim()
    .toLowerCase();
  const normalizedStatus = status
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
  return {
    key: id,
    txnId: id,
    type: ads ? 'ad' : collabs ? 'collab' : 'order',
    title: String(title),
    userId: String(
      ads
        ? record.advertiserId || record.bookedBy || ''
        : collabs
          ? record.bookedBy || ''
          : record.bookedBy || record.orderedBy || record.buyerId || ''
    ),
    userName: String(
      buyer.name ||
        (ads ? record.advertiserName : collabs ? record.bookedByname : record.orderedByname || record.bookedByname) ||
        ''
    ),
    ...(!ads
      ? {
          sellerId: String(record.userId || (!collabs && record.sellerId) || ''),
          sellerName: String(seller.name || (collabs && record.receiverName) || record.name || ''),
        }
      : {}),
    ...(!ads && !collabs ? { invoiceId: String(record.invoiceId || '') } : {}),
    status: ads ? status : collabs ? normalizedStatus : normalizedStatus.replace(/_/g, ' '),
    timeStamp: timestamp(record.timeStamp || record.dateCreated),
    amount: amount === undefined ? null : Number(amount),
    currency: currencyOf(record) || null,
    description: ads
      ? `Ad Campaign: ${record.adType || 'Unknown'}`
      : collabs
        ? `Collaboration: ${record.collabType || 'Standard'}`
        : key(record.itemType || record.feedType) === 'location'
          ? 'Location rental'
          : 'Store order',
    ...(ads ? { adType: String(record.adType || '') } : {}),
    metricTypes: transactionMetricTypes(domain, record),
  };
}
